/**
 * Renders user drawings and their selection handles.
 *
 * Holds no geometry logic of its own — it converts (time, price) anchors to
 * pixels and delegates to `drawingGeometry`, which the interaction layer uses
 * for hit-testing. One implementation of "where is this line", so what you can
 * grab is always exactly what you can see.
 */
import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import type { Drawing, PixelPoint } from './drawingGeometry';
import { drawingSegments, HANDLE_RADIUS } from './drawingGeometry';

const COLOR = '#42a5f5';
const COLOR_SELECTED = '#ffca28';
const PENDING_COLOR = 'rgba(66, 165, 245, 0.75)';

interface RenderItem {
  id: string;
  drawing: Drawing;
  points: PixelPoint[];
  selected: boolean;
}

class DrawingRenderer implements IPrimitivePaneRenderer {
  private _items: RenderItem[] = [];
  private _pending: PixelPoint[] = [];
  private _pendingTool: Drawing['tool'] | null = null;
  private _bounds = { width: 0, height: 0 };

  update(
    items: RenderItem[],
    pending: PixelPoint[],
    pendingTool: Drawing['tool'] | null,
    bounds: { width: number; height: number }
  ): void {
    this._items = items;
    this._pending = pending;
    this._pendingTool = pendingTool;
    this._bounds = bounds;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const hR = scope.horizontalPixelRatio as number;
      const vR = scope.verticalPixelRatio as number;

      for (const item of this._items) {
        this._strokeSegments(
          ctx,
          item.drawing,
          item.points,
          item.selected ? COLOR_SELECTED : COLOR,
          item.selected ? 2 : 1.5,
          hR,
          vR
        );
        // Handles only on the selected drawing: showing them on every line
        // turns a chart with a dozen drawings into a field of squares.
        if (item.selected) {
          ctx.save();
          ctx.fillStyle = COLOR_SELECTED;
          ctx.strokeStyle = 'rgba(0,0,0,0.6)';
          ctx.lineWidth = vR;
          for (const p of item.points) {
            const s = HANDLE_RADIUS * hR;
            ctx.beginPath();
            ctx.rect(p.x * hR - s, p.y * vR - s, s * 2, s * 2);
            ctx.fill();
            ctx.stroke();
          }
          ctx.restore();
        }
      }

      // The in-progress shape, so placing a multi-click drawing shows progress
      // instead of nothing until the final click.
      if (this._pendingTool && this._pending.length > 0) {
        ctx.save();
        ctx.setLineDash([4 * hR, 3 * hR]);
        this._strokeSegments(
          ctx,
          { tool: this._pendingTool } as Drawing,
          this._pending,
          PENDING_COLOR,
          1.5,
          hR,
          vR
        );
        for (const p of this._pending) {
          ctx.fillStyle = PENDING_COLOR;
          ctx.beginPath();
          ctx.arc(p.x * hR, p.y * vR, 3 * hR, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
    });
  }

  private _strokeSegments(
    ctx: CanvasRenderingContext2D,
    drawing: Pick<Drawing, 'tool'>,
    points: PixelPoint[],
    color: string,
    width: number,
    hR: number,
    vR: number
  ): void {
    const segs = drawingSegments(drawing.tool, points, this._bounds);
    if (segs.length === 0) return;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = width * vR;
    for (const seg of segs) {
      ctx.beginPath();
      ctx.moveTo(seg[0].x * hR, seg[0].y * vR);
      for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x * hR, seg[i].y * vR);
      ctx.stroke();
    }
    ctx.restore();
  }
}

class DrawingPaneView implements IPrimitivePaneView {
  constructor(private _source: DrawingPrimitive) {}

  zOrder(): PrimitivePaneViewZOrder {
    // Above the candles: a drawing the user placed should not be buried by
    // price action, unlike the GEX context lines which sit at the bottom.
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    return this._source.buildRenderer();
  }
}

export class DrawingPrimitive implements ISeriesPrimitive<Time> {
  private _paneView: DrawingPaneView;
  private _renderer = new DrawingRenderer();
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];

  private _drawings: Drawing[] = [];
  private _selectedId: string | null = null;
  private _pending: Drawing['anchors'] = [];
  private _pendingTool: Drawing['tool'] | null = null;

  constructor() {
    this._paneView = new DrawingPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
  }

  detached(): void {
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {}

  setState(
    drawings: Drawing[],
    selectedId: string | null,
    pending: Drawing['anchors'],
    pendingTool: Drawing['tool'] | null
  ): void {
    this._drawings = drawings;
    this._selectedId = selectedId;
    this._pending = pending;
    this._pendingTool = pendingTool;
    this._requestUpdate?.();
  }

  /**
   * Anchor -> pixels, or null when the anchor is off the current time scale.
   *
   * Public because the interaction layer must hit-test against exactly the same
   * projection the renderer used; recomputing it there would let the two drift.
   */
  toPixels(anchor: { time: number; price: number }): PixelPoint | null {
    const series = this._series;
    const chart = this._chart;
    if (!series || !chart) return null;
    const x = chart.timeScale().timeToCoordinate(anchor.time as Time);
    const y = series.priceToCoordinate(anchor.price);
    if (x === null || y === null) return null;
    return { x: x as number, y: y as number };
  }

  /** Pixels -> anchor, for placing and dragging. */
  toAnchor(x: number, y: number): { time: number; price: number } | null {
    const series = this._series;
    const chart = this._chart;
    if (!series || !chart) return null;
    const time = chart.timeScale().coordinateToTime(x);
    const price = series.coordinateToPrice(y);
    if (time === null || price === null) return null;
    return { time: time as unknown as number, price: price as number };
  }

  paneBounds(): { width: number; height: number } {
    const chart = this._chart;
    if (!chart) return { width: 0, height: 0 };
    return {
      width: chart.timeScale().width(),
      height: chart.paneSize?.().height ?? 0,
    };
  }

  /** Drawings with their current pixel projection, for hit-testing. */
  projected(): { drawing: Drawing; points: PixelPoint[] }[] {
    const out: { drawing: Drawing; points: PixelPoint[] }[] = [];
    for (const d of this._drawings) {
      const points: PixelPoint[] = [];
      let ok = true;
      for (const a of d.anchors) {
        const p = this.toPixels(a);
        if (!p) {
          ok = false;
          break;
        }
        points.push(p);
      }
      // A drawing whose anchors have scrolled off the time scale is skipped
      // rather than clamped — clamping would pin it to the edge and invite
      // dragging a line that is not really there.
      if (ok) out.push({ drawing: d, points });
    }
    return out;
  }

  buildRenderer(): IPrimitivePaneRenderer {
    const bounds = this.paneBounds();
    const items: RenderItem[] = this.projected().map(({ drawing, points }) => ({
      id: drawing.id,
      drawing,
      points,
      selected: drawing.id === this._selectedId,
    }));
    const pendingPts = this._pending
      .map((a) => this.toPixels(a))
      .filter((p): p is PixelPoint => p !== null);
    this._renderer.update(items, pendingPts, this._pendingTool, bounds);
    return this._renderer;
  }
}
